import { DateTime } from '@ksyos/date-time';
import {
    Box,
    Button,
    ButtonRow,
    FormButtonRow,
    Heading,
    PageHeader,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableCellSelectRow,
    TableContainer,
    TableHead,
    TableHeader,
    TableHeaderSelectAll,
    TableRow,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IPatientSatisfactionQuestionnaireMetadata } from '@common/interfaces/';
import type { ValidationErrors } from '@frontend/utils/validation';
import { ManagerAuthorization } from '@common/enums';
import {
    type IPatientSatisfactionFilters,
    patientSatisfactionFiltersValidation,
} from '@common/validation/patientQuestionnaireFilters';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization } from '@frontend/utils/assertHasAuthorization';
import { formatDateIntoString } from '@frontend/utils/formatDateIntoString';
import { handleValidatePeriod } from '@frontend/utils/validateDatePeriod';
import { emptyValidationErrors } from '@frontend/utils/validation';
import { PeriodFilter } from '../pages/finance/financing/financingSearchFilters/PeriodFilter';
import { PageTemplate } from '../templates/PageTemplate';
import { Figure } from './figure/Figure';

export function PatientQuestionnaireExport() {
    const [selectedQuestionnaireIds, setSelectedQuestionnaireIds] = useState<string[]>([]);
    const [allSelected, setAllSelected] = useState<boolean>(false);

    const [questionnaires, setQuestionnaires] = useState<IPatientSatisfactionQuestionnaireMetadata[]>([]);
    const [filters, setFilters] = useState<IPatientSatisfactionFilters>({});
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [total, setTotal] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [page, setPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(20);
    assertHasAuthorization(ManagerAuthorization.exportPatientQuestionnaires);

    useEffect(() => {
        load();
    }, [filters.startDate, filters.endDate, page, itemsPerPage]);

    return (
        <PageTemplate width="medium">
            <PageHeader title={translateKey('osasQuestionnaires.questionnaireReport')}></PageHeader>
            <Box
                display="flex"
                justifyContent="flex-end"
                mb={10}
            >
                <PeriodFilter
                    endDate={filters.endDate}
                    errors={errors}
                    onChangeDate={(name, value) => setFilters({ ...filters, [name]: value })}
                    onValidate={(name, value) =>
                        handleValidatePeriod({
                            datePeriod: {
                                endDate: name === 'endDate' ? value : filters.endDate,
                                startDate: name === 'startDate' ? value : filters.startDate,
                            },
                            errors,
                            schema: patientSatisfactionFiltersValidation,
                            setErrors,
                        })
                    }
                    startDate={filters.startDate}
                />
            </Box>

            {!!questionnaires.length && (
                <>
                    <TableContainer>
                        <Table spacing="medium">
                            <TableHead>
                                <TableRow>
                                    <TableHeaderSelectAll
                                        checked={!!selectedQuestionnaireIds.length || allSelected}
                                        indeterminate={!allSelected}
                                        onSelect={() => {
                                            setAllSelected(!allSelected);
                                            setSelectedQuestionnaireIds([]);
                                        }}
                                    />
                                    <TableHeader>{translateKey('service.service')}</TableHeader>
                                    <TableHeader>{translateKey('common.questionnaire')}</TableHeader>
                                    <TableHeader>{translateKey('common.completedOn')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {questionnaires.map((questionnaire) => (
                                    <TableRow key={`questionnaire_${questionnaire.id}`}>
                                        <TableCellSelectRow
                                            isSelected={
                                                allSelected || selectedQuestionnaireIds.includes(questionnaire.id)
                                            }
                                            onClick={(e) => e.stopPropagation()}
                                            onSelect={() => handleSelectRow(questionnaire.id)}
                                        />
                                        <TableCell>
                                            {translateKey(`episodeCategoryEnum.${questionnaire.episodeCategoryName}`)}
                                        </TableCell>
                                        <TableCell>{questionnaire.questionnaireId}</TableCell>
                                        <TableCell>
                                            {questionnaire.completionDate
                                                ? DateTime.from(questionnaire.completionDate).format('DD-MM-YYYY HH:mm')
                                                : ''}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Pagination
                        onChange={({ page, pageSize }) => {
                            setItemsPerPage(pageSize);
                            setPage(page);
                        }}
                        page={page}
                        pageSize={itemsPerPage}
                        totalItems={total}
                    />
                    <FormButtonRow>
                        <Button
                            iconLeft="download"
                            isDisabled={!selectedQuestionnaireIds.length && !allSelected}
                            onClick={handleDownloadExport}
                        >
                            {translateKey('common.download')}
                        </Button>
                    </FormButtonRow>
                </>
            )}

            {!questionnaires.length && !isLoading && (
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                >
                    <Figure
                        alt="Not found"
                        centered
                        imageUrl="../../../../../assets/images/not-found-search.svg"
                    />
                    <Heading
                        mb={6}
                        size="medium"
                        textAlign="center"
                    >
                        {translateKey('common.noSearchResults')}
                    </Heading>
                    <ButtonRow justify="center">
                        <Button onClick={() => setFilters({})}>{translateKey('common.deleteSearchTerms')}</Button>
                    </ButtonRow>
                </Box>
            )}
        </PageTemplate>
    );

    async function load() {
        setIsLoading(true);

        if (!errors.isEmpty()) {
            return;
        }

        const parsedFilters = {
            ...filters,
            endDate: filters.endDate ? formatDateIntoString(filters.endDate) : null,
            startDate: filters.startDate ? formatDateIntoString(filters.startDate) : null,
        };

        const result = await fetchJson(
            `/api/patientQuestionnaires?page=${page}&limit=${itemsPerPage}`,
            'POST',
            parsedFilters
        );

        if (!result.errorCode) {
            setQuestionnaires(result.json.items ?? []);
            setTotal(result.json.total ?? 0);
            setIsLoading(false);
        }
    }

    function handleSelectRow(id: string) {
        let newIds = [...selectedQuestionnaireIds];

        if (allSelected) {
            newIds = questionnaires
                .map((questionnaire) => questionnaire.id)
                .filter((questionnaireId) => questionnaireId !== id);
            setSelectedQuestionnaireIds(newIds);
            setAllSelected(false);

            return;
        } else if (selectedQuestionnaireIds.includes(id)) {
            const index = newIds.indexOf(id);
            newIds.splice(index, 1);
        } else {
            newIds.push(id);
        }

        setSelectedQuestionnaireIds(newIds);
    }

    async function handleDownloadExport() {
        const result = await fetchJson('/api/patientQuestionnaires/export', 'POST', {
            filters,
            questionnaireIds: selectedQuestionnaireIds,
        });

        window.open(`/api/exportDownloads/${result.json.exportDownloadId}/Rapportage_Vragenlijsten.xlsx`);
    }
}
